import React, { useEffect, useState } from 'react';
import { useAuth } from '../../../../../providers/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getWidgetById } from '../../../../../services/apiWidgests';
import Spinner from '../../../../../components/Spinner';
import { useDashboards } from '../../../../../providers/DashboardContext';
import DatabaseOffIco from '../../../../../components/icons/DatabaseOffIco';
import useWidgetRuntime from '../../../../../hooks/useWidgetRuntime';
import { useAppContext } from '../../../../../providers/AppContext';

const MetricWidget = ({ id }) => {
  const { addToast } = useAppContext();
  const { auth } = useAuth();
  const navigate = useNavigate();
  const { dashboard, selectedDatarange } = useDashboards();
  const { data: runtimeData, loading: fetchingWidgetData } = useWidgetRuntime(id, dashboard?._id, undefined, JSON.stringify(selectedDatarange));
  const [config, setConfig] = useState({});
  const [widgetTitle, setWidgetTitle] = useState("");
  const [fetchingWidgetInfo, setFetchingWidgetInfo] = useState(true);
  const [value, setValue] = useState({});

  // 👇 fuerza actualización del tiempo cada 1s
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setTick(t => t + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const parseCustomDate = (dateStr) => {
    const match = dateStr?.match(
      /^(\d{2})\/(\d{2})\/(\d{4})[\sT](\d{2}):(\d{2}):(\d{2})$/
    );

    if (!match) return new Date(dateStr);

    const [_, dd, mm, yyyy, hh, min, ss] = match;
    return new Date(`${yyyy}-${mm}-${dd}T${hh}:${min}:${ss}`);
  };

  // =============================================
  // 🔥 FORMATEAR "HACE X TIEMPO"
  // =============================================
  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return "";

    const past = parseCustomDate(dateStr);
    const now = new Date();

    let diff = Math.floor((now - past) / 1000);
    if (diff < 1) return "hace 1 segundo";

    const units = [
      { label: "año", plural: "años", seconds: 365 * 24 * 3600 },
      { label: "mes", plural: "meses", seconds: 30 * 24 * 3600 },
      { label: "día", plural: "días", seconds: 24 * 3600 },
      { label: "hora", plural: "horas", seconds: 3600 },
      { label: "minuto", plural: "minutos", seconds: 60 },
      { label: "segundo", plural: "segundos", seconds: 1 }
    ];

    let result = [];

    for (const u of units) {
      const val = Math.floor(diff / u.seconds);
      if (val > 0) {
        result.push(val + " " + (val === 1 ? u.label : u.plural));
        diff -= val * u.seconds;
      }
      if (result.length === 2) break;
    }

    return "hace " + result.join(" ");
  };

  const dataDisplayOptions = {
    "lastValue": "Último valor",
    "add": "Suma",
    "mean": "Promedio",
    "max": "Máximo",
    "min": "Mínimo",
  };


  //  Obtener info del widget
  const refreshWidgetInfo = async () => {
    try {
      setFetchingWidgetInfo(true);
      const response = await getWidgetById(id, auth?.token);
      const conf = response?.configuration || {};
      setConfig(conf);
      setWidgetTitle(response?.title);
      setFetchingWidgetInfo(false);
    } catch (error) {
      if (!error || !error.response) {
        addToast("No se pudo completar la consulta. Inténtalo nuevamente", 2);
        if (typeof setFetchingWidgetInfo !== 'undefined') setFetchingWidgetInfo(false);
        return;
      }
      if (error?.response?.status === 404) console.log("Widget no encontrado");
      else if (error?.response?.status === 500) console.log("Error en el servidor");
      else navigate('/logout');
      setFetchingWidgetInfo(false);
    }
  };

  useEffect(() => {
    refreshWidgetInfo();
  }, [selectedDatarange, id]);

  // Los datos ya no se piden por REST/polling: useWidgetRuntime los recibe
  // por socket desde el singleton compartido del backend (un solo tick cada
  // 5s en el servidor, sin importar cuántos dashboards miren este widget).
  // El backend (buildGroupedProcessMetricResponse) ya devuelve los grupos
  // ordenados alfabéticamente en cada tick, así que no hace falta "congelar"
  // el orden de la primera carga: eso hacía que un grupo nuevo (una máquina
  // que recién arranca a reportar, por ejemplo) solo apareciera después de
  // refrescar la página a mano.
  useEffect(() => {
    if (!runtimeData) return;
    setValue(runtimeData);
  }, [runtimeData]);


  // ====================================================
  // 🔥 BLOQUE IDEAL
  // ====================================================
  const renderIdealBlock = (val, idealVal, units) => {
    if (idealVal == null) return null;

    return (
      <span className="text-gray-500 text-sm font-semibold">
        Objetivo: <span className="text-black">{val}</span> / Ideal:{" "}
        <span className="text-blue-600">{idealVal}</span> {units}
      </span>
    );
  };


  // ====================================================
  // 🔥 MODO AGRUPADO
  // ====================================================
  const renderGrouped = () => {
    const groups = value.groups || [];

    return (

      <div className="w-full h-full flex flex-col gap-3 p-2 overflow-auto">
        {groups.length === 0 ?
          <div className='w-full h-full flex flex-col items-center justify-center -mt-8'>
            <span className='text-lg text-gray-700'>No hay datos por el momento</span>
            <DatabaseOffIco width='40px' height='40px'  fill='gray'/>
          </div>
          :
          <>
            <span className='text-gray-600 text-lg font-bold text-center'>
              {dataDisplayOptions[groups[0]?.dataDisplay] || "--"}
            </span>

            <div className="grid grid-cols-[repeat(auto-fill,minmax(120px,1fr))] gap-2 w-full">
              {groups.map((g, idx) => (
                <div
                  key={g.group ?? idx}
                  className="flex flex-col items-center justify-center border border-gray-300 rounded-lg p-2 shadow-sm bg-white"
                >
                  <span className="text-gray-600 text-xs font-semibold">{g.group}</span>

                  <span className="text-bluesens text-xl font-bold">
                    {g?.idealValue ? `${g.value}/${g.idealValue}` : g.value ?? "--"} {value?.units}
                  </span>

                  <span className="text-gray-500 text-xs">
                    {formatTimeAgo(g.lastUpdated)}
                  </span>
                </div>
              ))}
            </div>
          </>
        }

      </div>
    );
  };


  // ====================================================
  // 🔥 MODO NO AGRUPADO
  // ====================================================
  const renderSingle = () => (
    <div className='w-full h-full flex justify-center items-center'>
      <div className='flex flex-col w-full items-center gap-1'>
        <span className='text-gray-600 text-lg font-bold'>
          {dataDisplayOptions[value?.dataDisplay] || "--"}
        </span>

        <span className='text-bluesens text-4xl font-bold text-center'>
          {value?.value || "--"} {value?.units}
        </span>

        {renderIdealBlock(value?.value, value?.idealValue, value?.units)}

        <span className='text-gray-600 text-lg font-bold'>
          {formatTimeAgo(value?.lastUpdated)}
        </span>
      </div>
    </div>
  );

  return (
    <div className="w-full h-full p-4 bg-white border border-gray-300 rounded">
      {fetchingWidgetData ? (
        <div className='w-full h-full flex justify-center'>
          <Spinner />
        </div>
      ) : (
        <>
          {value?.isGrouped ? renderGrouped() : renderSingle()}
        </>
      )}
    </div>
  );
};

export default MetricWidget;
